import React, { useMemo, memo, useState, useEffect } from 'react';
import { useTranslation } from '~/shared/utils/translation';
import { FilterGroupAttributes, FiltersGroup } from '../FiltersGroup';
import { SelectedFilters } from '../SelectedFilters';
import {
    StyledDynamicFilters,
    StyledFiltersOptions,
    StyledOptionsButton,
    StyledFiltersPicker,
    StyledResponsiveTriggers,
    StyledResponsiveTriggersButton,
    StyledSelectedFiltersWrapper,
    StyledAttributeGroups,
    StyledOpenFilterContainer,
    StyledAttributes,
    StyledAttribute,
    StyledResponsiveCount,
} from './styled';
import FilterIcon from '$icons/filter.svg';
import SortIcon from '$icons/sort.svg';
import { Line } from '~/shared/components';
import { TranslationKey } from '~/lib/data-contract';
import { FiltersDrawer } from '../FiltersDrawer';
import { SortBy } from '../../SortBy';
import { useAlgoliaConfig } from '~/shared/hooks/useAlgoliaConfig';
import { connectDynamicWidgets, DynamicWidgetsProvided } from 'react-instantsearch-core';
import { useTheme } from '@emotion/react';
import { useFilterUIState } from '~/features/productsList/hooks';
import { debounce, isBrowser } from '$shared/utils';
import { ResultsCount } from './components/ResultsCount';

type CustomDynamicFiltersProps = DynamicWidgetsProvided;

const CustomDynamicFilters = memo(({ attributesToRender }: CustomDynamicFiltersProps) => {
    const {
        filtersCount,
        isFilterPanelOpen,
        setIsFilterPanelOpen,
        setIsSortDrawerOpen,
        setIsMobileFilterDrawerOpen,
        isFilterDrawerOpen,
        isSortDrawerOpen,
        preSelectedIndexKey,
        iconicFilterButtonsIsOutOfView,
    } = useFilterUIState();
    const { translate } = useTranslation();
    const { orderIndexes } = useAlgoliaConfig();
    const { selectedFilterGroup } = useFilterUIState();
    const { animations } = useTheme();
    const [scrollY, setScrollY] = useState(0);

    const filters = useMemo(() => {
        return attributesToRender.map((filter) => {
            return {
                name: filter,
                title: `commerce.filters.${filter}`,
            };
        });
    }, [attributesToRender]);

    useEffect(() => {
        if (isBrowser) {
            const handleScroll = () => {
                setScrollY(window.scrollY);
            };
            const debounceHandleScroll = debounce(handleScroll, 100);

            handleScroll();

            window.addEventListener('scroll', debounceHandleScroll);
            return () => {
                window.removeEventListener('scroll', debounceHandleScroll);
            };
        }
    }, []);

    return (
        <>
            <StyledDynamicFilters>
                <StyledFiltersPicker>
                    <StyledAttributeGroups>
                        {filters.slice(0, 5).map((filter: { name: string; title: string }) => {
                            return (
                                <FiltersGroup
                                    key={filter.name}
                                    attribute={filter.name}
                                    title={translate(filter.title as TranslationKey)}
                                />
                            );
                        })}
                    </StyledAttributeGroups>

                    <StyledFiltersOptions>
                        <StyledOptionsButton
                            onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
                        >
                            {translate('commerce.filters.viewAll')}
                            <FilterIcon aria-hidden="true" />
                        </StyledOptionsButton>
                        <SortBy
                            defaultRefinement={preSelectedIndexKey || orderIndexes[0].key}
                            items={orderIndexes}
                        />
                    </StyledFiltersOptions>
                </StyledFiltersPicker>

                <Line />

                <StyledOpenFilterContainer
                    initial="collapsed"
                    animate={isFilterDrawerOpen ? 'open' : 'collapsed'}
                    exit="collapsed"
                    variants={{
                        open: {
                            display: '',
                            height: 'auto',
                        },
                        collapsed: {
                            height: 0,
                            transitionEnd: {
                                display: 'none',
                            },
                        },
                    }}
                    transition={{
                        ...animations.springDefault,
                    }}
                >
                    <StyledAttributes>
                        {filters.map(({ name }) => (
                            <StyledAttribute
                                style={{ display: selectedFilterGroup === name ? 'block' : 'none' }}
                                key={name}
                            >
                                <FilterGroupAttributes
                                    key={name}
                                    attribute={name}
                                    showMore
                                    showMoreLimit={100}
                                />
                            </StyledAttribute>
                        ))}
                    </StyledAttributes>
                </StyledOpenFilterContainer>

                {isFilterDrawerOpen && <Line />}

                <StyledSelectedFiltersWrapper>
                    <SelectedFilters />
                    <StyledResponsiveCount variant="desktop">
                        <ResultsCount spacing="header" />
                    </StyledResponsiveCount>
                </StyledSelectedFiltersWrapper>
            </StyledDynamicFilters>

            <StyledResponsiveTriggers shouldShow={iconicFilterButtonsIsOutOfView && scrollY > 200}>
                <StyledResponsiveTriggersButton
                    variant="primary"
                    shade="light"
                    size="md"
                    onClick={() => {
                        setIsFilterPanelOpen(true);
                        setIsMobileFilterDrawerOpen(true);
                    }}
                >
                    <FilterIcon aria-hidden="true" />
                    {translate('commerce.filters.filter')} ({filtersCount})
                </StyledResponsiveTriggersButton>
                <StyledResponsiveTriggersButton
                    variant="primary"
                    shade="light"
                    size="md"
                    onClick={() => {
                        setIsSortDrawerOpen(!isSortDrawerOpen);
                        setIsMobileFilterDrawerOpen(true);
                    }}
                >
                    <SortIcon aria-hidden="true" />
                    {translate('commerce.sort.sort')}
                </StyledResponsiveTriggersButton>
            </StyledResponsiveTriggers>

            <FiltersDrawer
                isOpen={isFilterPanelOpen}
                onClose={() => {
                    setIsFilterPanelOpen(false);
                    setIsMobileFilterDrawerOpen(false);
                }}
            ></FiltersDrawer>
        </>
    );
});

export const DynamicFilters = connectDynamicWidgets<CustomDynamicFiltersProps>(
    CustomDynamicFilters
);
